import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import type { AlertType } from './useInlineAlert';

const SESSION_TIMEOUT_WARNING = (24 * 60 - 10) * 60 * 1000; // 23h 50min
const SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];

export interface SecuritySessionAlert {
  type: AlertType;
  message: string;
  title?: string;
}

export const useSecuritySession = () => {
  const { user, signOut } = useAuth();
  const lastActivityRef = useRef(Date.now());
  const [lastActivityDisplay, setLastActivityDisplay] = useState(Date.now());
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [lastAlert, setLastAlert] = useState<SecuritySessionAlert | null>(null);
  const showTimeoutWarningRef = useRef(false);

  const clearAlert = useCallback(() => setLastAlert(null), []);

  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setLastActivityDisplay(Date.now());
    setShowTimeoutWarning(false);
    showTimeoutWarningRef.current = false;
  }, []);

  const handleSessionTimeout = useCallback(async () => {
    setLastAlert({
      type: 'error',
      title: 'Sesión expirada',
      message: 'Tu sesión ha expirado por inactividad. Por favor inicia sesión nuevamente.'
    });
    await signOut();
  }, [signOut]);

  // Session timeout disabled — users should never be auto-logged out by inactivity.
  useEffect(() => {
    if (!user) return;
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });
    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [user, updateActivity]);


  return {
    lastActivity: lastActivityDisplay,
    showTimeoutWarning,
    extendSession: updateActivity,
    lastAlert,
    clearAlert
  };
};

// Utility to mask sensitive data
export const maskSensitiveData = {
  // Mask email: j***n@example.com
  email: (email: string): string => {
    if (!email || !email.includes('@')) return '***';
    const [local, domain] = email.split('@');
    if (local.length <= 2) return `${local[0]}***@${domain}`;
    return `${local[0]}***${local[local.length - 1]}@${domain}`;
  },

  // Mask phone: ***-***-1234
  phone: (phone: string): string => {
    if (!phone || phone.length < 4) return '***';
    const cleaned = phone.replace(/\D/g, '');
    return `***-***-${cleaned.slice(-4)}`;
  },

  // Mask IBAN: CR***...1234
  iban: (iban: string): string => {
    if (!iban || iban.length < 8) return '***';
    return `${iban.slice(0, 2)}***...${iban.slice(-4)}`;
  },

  // Mask cedula/RNC: ***-1234
  cedula: (cedula: string): string => {
    if (!cedula || cedula.length < 4) return '***';
    return `***-${cedula.slice(-4)}`;
  },

  // Mask credit card: **** **** **** 1234
  creditCard: (card: string): string => {
    if (!card || card.length < 4) return '***';
    const cleaned = card.replace(/\D/g, '');
    return `**** **** **** ${cleaned.slice(-4)}`;
  }
};

// Sanitize user input to prevent XSS
export const sanitizeInput = (input: string): string => {
  if (!input) return '';
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

// Validate URL to prevent open redirects
export const isValidRedirectUrl = (url: string): boolean => {
  if (!url) return false;
  
  // Only allow relative URLs or same-origin URLs
  if (url.startsWith('/') && !url.startsWith('//')) {
    return true;
  }
  
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.origin === window.location.origin;
  } catch {
    return false;
  }
};
